import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { bulkModerateReports } from '../services/firestoreService';
import { useAdminReports, STATUS_FILTERS } from '../hooks/useAdminReports';

function formatDate(value) {
  if (!value) return '—';
  const date = value.toDate?.() ?? (typeof value === 'string' ? new Date(value) : null);
  if (!date || Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
}

function statusBadgeClass(status) {
  if (status === 'approved') return 'bg-green-100 text-green-800';
  if (status === 'rejected') return 'bg-yellow-100 text-yellow-800';
  if (status === 'deleted') return 'bg-gray-200 text-gray-700';
  if (status === 'pending') return 'bg-blue-100 text-blue-800';
  return 'bg-gray-100 text-gray-700';
}

function rowActions(status) {
  if (status === 'approved') {
    return {
      primary: { action: 'reject', label: 'Unapprove', class: 'bg-yellow-600 hover:bg-yellow-700' },
      secondary: { action: 'delete', label: 'Delete', class: 'bg-red-600 hover:bg-red-700' },
    };
  }
  if (status === 'deleted') {
    return { primary: null, secondary: null };
  }
  return {
    primary: { action: 'approve', label: 'Approve', class: 'bg-green-600 hover:bg-green-700' },
    secondary: { action: 'delete', label: 'Delete', class: 'bg-red-600 hover:bg-red-700' },
  };
}

function actionVerb(action) {
  if (action === 'approve') return 'Approved';
  if (action === 'reject') return 'Unapproved';
  if (action === 'delete') return 'Deleted';
  return 'Updated';
}

function ReportTable({
  reports,
  selectedIds,
  pendingIds,
  onToggle,
  onToggleAll,
  onModerate,
}) {
  const allSelected = reports.length > 0 && selectedIds.size === reports.length;
  const someSelected = selectedIds.size > 0;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 w-10">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected && !allSelected;
                }}
                onChange={(e) => onToggleAll(e.target.checked)}
                aria-label="Select all reports"
                className="rounded border-gray-300"
              />
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Phone
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Status
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Reporter
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Category
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Comment
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Date
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {reports.map((report) => {
            const { primary, secondary } = rowActions(report.status);
            const rowPending = pendingIds.has(report.id);
            return (
              <tr
                key={report.id}
                className={
                  selectedIds.has(report.id)
                    ? 'bg-blue-50'
                    : rowPending
                      ? 'opacity-60'
                      : undefined
                }
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedIds.has(report.id)}
                    onChange={() => onToggle(report.id)}
                    disabled={rowPending}
                    aria-label={`Select report ${report.id}`}
                    className="rounded border-gray-300"
                  />
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  <Link
                    to={`/number/${report.phoneNumberId}`}
                    className="text-blue-600 hover:underline"
                  >
                    +{report.phoneNumberId}
                  </Link>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${statusBadgeClass(report.status)}`}
                  >
                    {report.status || 'unknown'}
                  </span>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  {report.reporterEmail || report.reporterId || 'Anonymous'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  {report.categoryName || report.categoryId}
                </td>
                <td className="px-4 py-3 max-w-xs text-sm truncate" title={report.comment}>
                  {report.comment}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(report.createdAt)}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm">
                  {primary && (
                    <button
                      type="button"
                      disabled={rowPending}
                      onClick={() => onModerate([report.id], primary.action)}
                      className={`mr-2 px-3 py-1 rounded text-white disabled:opacity-50 ${primary.class}`}
                    >
                      {primary.label}
                    </button>
                  )}
                  {secondary && (
                    <button
                      type="button"
                      disabled={rowPending}
                      onClick={() => onModerate([report.id], secondary.action)}
                      className={`px-3 py-1 rounded text-white disabled:opacity-50 ${secondary.class}`}
                    >
                      {secondary.label}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AdminDashboard() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [phoneInput, setPhoneInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({
    status: 'all',
    phoneNumberId: '',
  });
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [pendingIds, setPendingIds] = useState(() => new Set());
  const [statusMessage, setStatusMessage] = useState('');

  const {
    reports,
    initialLoading,
    tableLoading,
    error,
    setError,
    refresh,
    patchReports,
    rollbackReports,
  } = useAdminReports(appliedFilters);

  useEffect(() => {
    const t = setTimeout(() => {
      const digits = phoneInput.replace(/\D/g, '');
      setAppliedFilters((prev) => {
        if (prev.status === statusFilter && prev.phoneNumberId === digits) return prev;
        return { status: statusFilter, phoneNumberId: digits };
      });
    }, 300);
    return () => clearTimeout(t);
  }, [phoneInput, statusFilter]);

  useEffect(() => {
    setSelectedIds(new Set());
    setStatusMessage('');
  }, [appliedFilters.status, appliedFilters.phoneNumberId]);

  const toggleSelection = (reportId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(reportId)) next.delete(reportId);
      else next.add(reportId);
      return next;
    });
  };

  const toggleSelectAll = (checked) => {
    if (!checked) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(reports.map((r) => r.id)));
  };

  const handleModerate = useCallback(
    async (reportIds, action) => {
      if (!reportIds.length) return;

      const snapshot = reports;
      setError('');
      setStatusMessage('');
      setPendingIds((prev) => {
        const next = new Set(prev);
        reportIds.forEach((id) => next.add(id));
        return next;
      });
      patchReports(reportIds, action);

      const clearPending = () => {
        setPendingIds((prev) => {
          const next = new Set(prev);
          reportIds.forEach((id) => next.delete(id));
          return next;
        });
      };

      setSelectedIds((prev) => {
        const next = new Set(prev);
        reportIds.forEach((id) => next.delete(id));
        return next;
      });

      try {
        const result = await bulkModerateReports(reportIds, action);
        const updated = result?.updated ?? 0;
        setStatusMessage(
          `${actionVerb(action)} ${updated} report${updated === 1 ? '' : 's'}.`
        );
      } catch (err) {
        rollbackReports(snapshot);
        setError('Error moderating reports: ' + err.message);
      } finally {
        clearPending();
      }
    },
    [reports, patchReports, rollbackReports, setError]
  );

  const handleBulkAction = (action) => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    handleModerate(ids, action);
  };

  const bulkActions = useMemo(() => {
    const { status } = appliedFilters;
    if (status === 'approved') {
      return [
        { action: 'reject', label: 'Unapprove selected', class: 'bg-yellow-600 hover:bg-yellow-700' },
        { action: 'delete', label: 'Delete selected', class: 'bg-red-600 hover:bg-red-700' },
      ];
    }
    if (status === 'deleted') return [];
    return [
      { action: 'approve', label: 'Approve selected', class: 'bg-green-600 hover:bg-green-700' },
      { action: 'delete', label: 'Delete selected', class: 'bg-red-600 hover:bg-red-700' },
    ];
  }, [appliedFilters]);

  const selectionLabel = useMemo(() => {
    if (selectedIds.size === 0) return '';
    return `${selectedIds.size} selected`;
  }, [selectedIds.size]);

  if (initialLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <h1 className="text-2xl font-bold mb-2">Admin Dashboard</h1>
        <p className="text-gray-500">Loading reports…</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <h1 className="text-2xl font-bold mb-2">Admin Dashboard</h1>
      <p className="text-gray-600 mb-6">
        Manage community reports. Unapprove hides a report from the site but it still affects
        scores. Delete removes it from scoring.
      </p>

      {error && (
        <div className="text-red-600 mb-4 bg-red-50 border border-red-200 rounded px-4 py-2">
          {error}
        </div>
      )}
      {statusMessage && (
        <div className="text-green-800 mb-4 bg-green-50 border border-green-200 rounded px-4 py-2">
          {statusMessage}
        </div>
      )}

      <div className="flex flex-wrap gap-4 mb-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Phone number</label>
          <input
            type="text"
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            placeholder="e.g. 61412345678"
            className="border rounded px-3 py-2 text-sm w-48"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border rounded px-3 py-2 text-sm"
          >
            {STATUS_FILTERS.map((f) => (
              <option key={f.value} value={f.value}>
                {f.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {selectedIds.size > 0 && bulkActions.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-3 bg-gray-100 rounded-lg px-4 py-3">
          <span className="text-sm font-medium text-gray-700">{selectionLabel}</span>
          {bulkActions.map((btn) => (
            <button
              key={btn.action}
              type="button"
              disabled={pendingIds.size > 0}
              onClick={() => handleBulkAction(btn.action)}
              className={`text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-50 ${btn.class}`}
            >
              {btn.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setSelectedIds(new Set())}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            Clear selection
          </button>
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">
              Reports ({reports.length})
            </h2>
            {tableLoading && (
              <span className="text-sm text-gray-500">Updating…</span>
            )}
          </div>

          {tableLoading && reports.length === 0 ? (
            <p className="text-gray-500">Loading reports…</p>
          ) : reports.length === 0 ? (
            <p className="text-gray-500">No reports match these filters.</p>
          ) : (
            <ReportTable
              reports={reports}
              selectedIds={selectedIds}
              pendingIds={pendingIds}
              onToggle={toggleSelection}
              onToggleAll={toggleSelectAll}
              onModerate={handleModerate}
            />
          )}
        </div>
      </div>

      <div className="mt-4 flex gap-3">
        <button
          type="button"
          onClick={refresh}
          disabled={tableLoading}
          className="text-sm text-blue-600 hover:underline disabled:opacity-50"
        >
          Refresh list
        </button>
      </div>

      <p className="mt-6 text-sm text-gray-500">
        Set <code className="bg-gray-100 px-1">isAdmin: true</code> on your user document in
        Firestore to access this page. Deploy functions after updates:{' '}
        <code className="bg-gray-100 px-1">firebase deploy --only functions</code>
      </p>
    </div>
  );
}

export default AdminDashboard;
